import React from "react"
import PropTypes from "prop-types"
import { Flex, Popover, Switch } from "antd"
import { useTranslation } from "react-i18next"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import { Text } from "ui/Typography"
import { DSIcon } from "ui/Icon/DSIcon"
import { Icon } from "ui/Icon"
import moment from "moment"
import { RANGE_OPTIONS, CALENDAR_TABS } from "../../constants"
import styles from "../../Calendar.module.css"

const VIEW_OPTIONS = [
  { label: "Day View", key: "day", range: "date", listview: false },
  { label: "Week View", key: "week", range: "week", listview: false },
  { label: "Month View", key: "month", range: "month", listview: false },
  { label: "List View", key: "list", range: "week", listview: true }
]

const TABLET_RANGES = [
  { range: "date", label: "Day" },
  { range: "week", label: "Week" },
  { range: "month", label: "Month" }
]

const getViewIcon = (range, agenda) => {
  const isAgenda = agenda === true || agenda === "true"
  if (range === "date") return <DSIcon name="calendarDayView" width={18} height={18} fill="currentColor" />
  if (range === "month") return <DSIcon name="calendarMonthView" width={18} height={18} fill="currentColor" />
  if (range === "week" && isAgenda) return <DSIcon name="calendarListView" width={18} height={18} fill="currentColor" />
  return <DSIcon name="calendarWeekView" width={18} height={18} fill="currentColor" />
}

const FilterIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

const DateNavRow = ({
  params,
  activeTab,
  isDesktop,
  isTablet,
  isMobile,
  date,
  dateMoment,
  dateSwitcherKey,
  dateSwitcherType,
  filterBadgeCount,
  agendaChecked,
  viewPopoverOpen,
  setViewPopoverOpen,
  setMobileFiltersOpen,
  onChangeFilters,
  onRangeChange,
  onAgendaChange,
  onGoToToday
}) => {
  const { t } = useTranslation()
  const isListView = params.range === "week" && params.listview

  return (
    <Flex gap={8} align="center" justify="flex-start" className={`${styles.calendarActionsWrapper} ${isDesktop ? "" : "px-m py-xs"}`} wrap>
      {isDesktop && (
        <Flex align="center" gap={32} className={styles.dateSwitcherFlex} wrap>
          {/* "Today" leads the date controls, as the design shows, rather than sitting off to the
              right of the row — the right-hand side is the filters' now. */}
          <Flex align="center" gap={4}>
            <button
              type="button"
              className={styles.todayButton}
              disabled={!dateMoment.isValid() || dateMoment.isSame(moment(), "day")}
              onClick={onGoToToday}
            >
              <Icon name="RotateCcw" width={16} height={16} color="currentColor" />
              <span>{t("calendar.today", "Today")}</span>
            </button>
            <DateSwitcher
              key={dateSwitcherKey}
              className={styles.calendarSwitcher}
              value={date}
              defaultValue={date}
              name="date"
              onChange={onChangeFilters}
              type={params.range}
              isCompleteDateRange
            />
          </Flex>
          <Flex align="center" gap={4}>
            {Object.entries(RANGE_OPTIONS).map(([range, label]) => (
              <button
                key={range}
                type="button"
                className={`${styles.viewTab} ${params.range === range ? styles.viewTabActive : ""}`}
                onClick={() => onRangeChange(range)}
              >
                {label}
              </button>
            ))}
          </Flex>
          {params.range === "week" && activeTab === CALENDAR_TABS.ALL_BOOKINGS && (
            <Flex align="center" gap={8} className={styles.agendaToggle}>
              <Switch checked={agendaChecked} onChange={onAgendaChange} size="small" />
              <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
            </Flex>
          )}
        </Flex>
      )}

      {isTablet && (
        <Flex align="center" justify="space-between" gap={8} className={`${styles.crewInlineRow} w-full`}>
          <Flex align="center" gap={8}>
            <Flex align="center" gap={0} className={styles.agendaToggle}>
              {TABLET_RANGES.map(({ range: r, label }) => (
                <button
                  key={r}
                  type="button"
                  className={`${styles.mobileViewTab} ${params.range === r ? styles.mobileViewTabActive : ""}`}
                  onClick={() => onChangeFilters({ target: { name: "range", value: r }, extra: { name: "listview", value: false } })}
                >
                  {label}
                </button>
              ))}
            </Flex>
            {params.range === "week" && activeTab === CALENDAR_TABS.ALL_BOOKINGS && (
              <Flex align="center" gap={8} className={styles.agendaToggle}>
                <Switch checked={agendaChecked} onChange={onAgendaChange} size="small" />
                <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
              </Flex>
            )}
          </Flex>
          <Flex align="center" gap={8} className="ml-auto">
            <Flex align="center" className={`${styles.dateSwitcherMiddle} flex-row-reverse`}>
              <DateSwitcher
                key={dateSwitcherKey}
                className={styles.calendarSwitcher}
                value={date}
                defaultValue={date}
                name="date"
                onChange={onChangeFilters}
                type={dateSwitcherType}
                isCompleteDateRange
              />
              {activeTab !== CALENDAR_TABS.CREW && (
                <button
                  type="button"
                  className={`${styles.mobileHeaderIcon} ${styles.iconBtnReset} ${styles.mobileFilters}`}
                  onClick={() => setMobileFiltersOpen(true)}
                  aria-label="Filters"
                >
                  <FilterIcon />
                  {filterBadgeCount > 0 && <span className={styles.filterBadge}>{filterBadgeCount}</span>}
                </button>
              )}
            </Flex>

            <button
              type="button"
              className={`${styles.mobileHeaderIcon} ${styles.iconBtnReset}`}
              onClick={() => setMobileFiltersOpen(true)}
              aria-label="Filters"
            >
              <FilterIcon />
              {filterBadgeCount > 0 && <span className={styles.filterBadge}>{filterBadgeCount}</span>}
            </button>
          </Flex>
        </Flex>
      )}

      {isMobile && (
        <Flex align="center" justify="space-between" wrap gap={12} className={`${styles.crewInlineRow} w-full`}>
          <div className={styles.dateSwitcherFlex}>
            <DateSwitcher
              key={dateSwitcherKey}
              className={styles.calendarSwitcher}
              value={date}
              defaultValue={date}
              name="date"
              onChange={onChangeFilters}
              type={dateSwitcherType}
              isCompleteDateRange
            />
          </div>
          <Flex align="center" gap={8} className="ml-auto">
            <button
              type="button"
              className={`${styles.mobileHeaderIcon} ${styles.iconBtnReset}`}
              onClick={() => setMobileFiltersOpen(true)}
              aria-label="Filters"
            >
              <FilterIcon />
              {filterBadgeCount > 0 && <span className={styles.filterBadge}>{filterBadgeCount}</span>}
            </button>

            <Popover
              open={viewPopoverOpen}
              onOpenChange={setViewPopoverOpen}
              trigger="click"
              placement="bottomRight"
              arrow={false}
              overlayClassName={styles.viewSwitcherPopoverOverlay}
              content={
                <div className={styles.viewSwitcherPopup}>
                  {VIEW_OPTIONS.map(({ label, key, range, listview: lv }) => {
                    const isExplicit = params.listview === true || params.listview === "true"
                    const isActive = lv ? params.range === range && isExplicit : params.range === range && !isExplicit
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.viewSwitcherOption} ${isActive ? styles.viewSwitcherOptionActive : ""}`}
                        onClick={() => {
                          onChangeFilters({ target: { name: "range", value: range }, extra: { name: "listview", value: lv } })
                          setViewPopoverOpen(false)
                        }}
                      >
                        <span className={styles.viewSwitcherOptionLabel}>{label}</span>
                      </button>
                    )
                  })}
                </div>
              }
            >
              <button type="button" className={`${styles.mobileHeaderIcon} ${styles.iconBtnPrimary}`} aria-label="Switch view">
                {getViewIcon(params.range, params.agenda)}
              </button>
            </Popover>
          </Flex>
        </Flex>
      )}

      {isMobile && params.range === "week" && activeTab === CALENDAR_TABS.ALL_BOOKINGS && !isListView && (
        <Flex align="center" gap={8} className={styles.agendaToggle}>
          <Switch checked={agendaChecked} onChange={onAgendaChange} size="small" />
          <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
        </Flex>
      )}
    </Flex>
  )
}

DateNavRow.propTypes = {
  params: PropTypes.object.isRequired,
  activeTab: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
  isTablet: PropTypes.bool,
  isMobile: PropTypes.bool,
  date: PropTypes.string.isRequired,
  dateMoment: PropTypes.object.isRequired,
  dateSwitcherKey: PropTypes.string.isRequired,
  dateSwitcherType: PropTypes.string,
  filterBadgeCount: PropTypes.number,
  agendaChecked: PropTypes.bool,
  viewPopoverOpen: PropTypes.bool,
  setViewPopoverOpen: PropTypes.func.isRequired,
  setMobileFiltersOpen: PropTypes.func.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  onAgendaChange: PropTypes.func.isRequired,
  onGoToToday: PropTypes.func.isRequired
}

export default DateNavRow
